import useAuth from '@app/auth/use-auth';
import { BulkActionBar } from '@app/components/data-table/bulk-edit/bulk-action-bar';
import { makeSelectColumn } from '@app/components/data-table/bulk-edit/select-column';
import { DataTable } from '@app/components/data-table/data-table';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import { CurrentItemType } from '@app/components/excluded-items/use-change-item-type';
import { UpdateItemButton } from '@app/components/item-edit/update-item-button';
import { makeUpdateItemsAction } from '@app/components/item-edit/update-items-modal-body';
import { useDataTableQueryState } from '@app/hooks/use-data-table-query-state';
import { useGetSoldUncategorisedList } from '@shared/api/hooks/sold-goods/uncategorised';
import {
  OperationReportTypeEnum,
  SoldGoodsUncategorisedListItemType,
  SoldGoodsUncategorisedListParams,
  UncategorisedSoldGood,
} from '@shared/api/types';
import { ActivityCell } from '@shared/components/data-table/cells/activity-cell';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { uncategorisedFilterColumns, uncategorisedFilters } from './filters';
import { ExcludedItemType } from './tabs';

interface ExcludedSoldItemsTabProps {
  type: ExcludedItemType;
}

// Maps a sortable column id to the endpoint's `ordering` field name.
const ORDERING_BY_COLUMN: Record<string, string> = {
  name: 'name',
  quantity: 'total_aggregated_quantity',
};

/**
 * Listing for one sold-goods Excluded items tab (Uncertain / Other).
 *
 * Sold excluded goods are materialised as zero-emission other-purchased-goods
 * operation activities, so this mirrors the purchased hub: it is activity-keyed
 * (rows carry Quantity aggregated over the records) and supports the Site /
 * Time period / Customer / Import facets. Sold goods have no Supplier or Tags
 * facet.
 * Total spend is intentionally omitted — spend isn't fully supported for sold
 * goods yet (the purchased hub still shows it). The bulk "change type" actions
 * operate on the SoldGood, so they use `soldGoodUuid` (the row `uuid` is the
 * OperationActivity uuid).
 */
export default function ExcludedSoldItemsTab({
  type,
}: ExcludedSoldItemsTabProps) {
  const { page, perPage, sort, search, ...filterStates } =
    useDataTableQueryState({
      filters: ['search', ...uncategorisedFilterColumns],
    });
  const { user } = useAuth();

  // The endpoint takes a single `ordering` directive (prefix '-' for
  // descending); map the first sort directive, falling back to the backend
  // default (newest first).
  const firstSort = sort?.[0];
  const orderingField = firstSort
    ? ORDERING_BY_COLUMN[firstSort.id]
    : undefined;
  const ordering = orderingField
    ? firstSort?.desc
      ? `-${orderingField}`
      : orderingField
    : undefined;

  const params: SoldGoodsUncategorisedListParams = {
    item_type:
      type === 'uncertain'
        ? SoldGoodsUncategorisedListItemType.uncertain
        : SoldGoodsUncategorisedListItemType.other,
    search: search ?? undefined,
    page: page ? String(page) : undefined,
    page_size: perPage ? String(perPage) : undefined,
    ordering,
  };
  const paramRecord = params as Record<string, unknown>;
  const filterRecord = filterStates as Record<string, unknown>;
  uncategorisedFilters.forEach(({ apiKey, column }) => {
    paramRecord[apiKey] = filterRecord[column];
  });

  const { data, isLoading, isPreviousData, isFetchedAfterMount } =
    useGetSoldUncategorisedList(params);

  const pageCount = data?.count ? Math.ceil(data.count / (perPage || 10)) : 0;

  const columns = useMemo<ColumnDef<UncategorisedSoldGood>[]>(
    () => [
      // Selection checkboxes are staff-only (bulk actions are gated to staff).
      ...(user?.isStaff ? [makeSelectColumn<UncategorisedSoldGood>()] : []),
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => <HeaderCell column={column} name="Name" />,
        cell: ({ row }) => (
          <ActivityCell
            name={row.original.name}
            remoteId={row.original.externalId ?? undefined}
          />
        ),
        size: 280,
        meta: { label: 'Name', sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' } },
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: ({ column }) => (
          <HeaderCell column={column} name="Quantity" numberColumn />
        ),
        cell: ({ getValue }) => {
          const value = getValue<number | null>();
          return value == null ? (
            <TextCell text="—" type="number" />
          ) : (
            <NumberCell number={value} rounded />
          );
        },
      },
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <UpdateItemButton
              itemUuid={row.original.soldGoodUuid}
              currentType={type}
              reportType={OperationReportTypeEnum.sales}
              itemDetails={{
                name: row.original.name,
                externalId: row.original.externalId,
                inputUnit: row.original.inputUnit,
                itemsPerPack: row.original.itemsPerPack,
                itemMassOrVolume: row.original.itemMassOrVolume,
                itemUnitSymbol: row.original.itemUnit,
                inputMode: row.original.inputMode,
                quantity: row.original.quantity,
                totalSpend: row.original.totalSpend,
              }}
            />
          </div>
        ),
      },
    ],
    [type, user?.isStaff]
  );

  const { table } = useServerDataTable<UncategorisedSoldGood>({
    data: data?.results || [],
    columns,
    pageCount,
    shallow: false,
    getRowId: (row) => row.soldGoodUuid,
  });

  const currentType: CurrentItemType = type;
  const bulkActions = useMemo(
    () => [
      makeUpdateItemsAction({
        currentType,
        reportType: OperationReportTypeEnum.sales,
      }),
    ],
    [currentType]
  );

  return (
    <>
      <DataTable
        table={table}
        className="border border-gray-100 bg-gray-00 text-left"
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
      />
      <BulkActionBar table={table} actions={bulkActions} />
    </>
  );
}
